import { AccessRecipe, AccessFilterCondition } from 'src/app/hickory-api';
import { Filter } from '../filtered-search/filtered-search.component';
import { FilterType, SearchFilter } from 'src/app/handweave-api';

export type AccessFilterExpression = 'oneOf' | 'wildcard';
export class AccessExpression {
  static readonly OneOf = 'oneOf';
  static readonly Wildcard = 'wildcard';

  static isValid(expression: string): boolean {
    if (!expression?.trim()?.length) return false;

    return Object.values(AccessExpression).includes(expression as AccessFilterExpression);
  }
}

export class AccessRecipeHelper {
  static toSearchFilter(accessRecipe: AccessRecipe): SearchFilter[] {
    if (!accessRecipe?.filter?.conditions?.length) {
      return [];
    }

    return accessRecipe?.filter?.conditions?.map((condition) => ({
      field: condition.field,
      filterType: FilterType.Terms,
      values: condition.values,
      expression: condition.expression || 'oneOf',
    }));
  }

  static toSearchFilters(accessRecipes: AccessRecipe[]): SearchFilter[] {
    if (!accessRecipes?.length) {
      return [];
    }

    return accessRecipes?.flatMap((accessRecipe) => this.toSearchFilter(accessRecipe));
  }

  static toFilter(accessRecipe: AccessRecipe, valueMap?: (values: string[]) => unknown): Filter[] {
    return accessRecipe?.filter?.conditions?.map((condition) => ({
      field: condition.field,
      value: valueMap ? valueMap(condition.values) : condition.values?.join(','),
      expression: condition.expression || 'oneOf',
    }));
  }

  static toFilters(accessRecipes: AccessRecipe[], valueMap?: (values: string[]) => unknown): Filter[] {
    if (!accessRecipes?.length) {
      return [];
    }

    return accessRecipes?.flatMap((accessRecipe) => this.toFilter(accessRecipe, valueMap));
  }

  static fromFilters(filters: Filter[], valueMap?: (value: string) => string[]): AccessRecipe[] {
    if (!filters?.length) {
      return [];
    }

    const getValue = (filter: Filter): string[] => {
      if (valueMap) {
        return valueMap(filter.value as string);
      }

      if (Array.isArray(filter.value)) {
        return filter.value;
      }

      return [filter.value as string];
    };

    return <AccessRecipe[]>filters?.map((filter) => ({
      filter: {
        conditions: [
          {
            field: filter.field,
            values: getValue(filter),
            expression: 'oneOf',
          },
        ],
      },
    }));
  }

  static validRecipes(accessRecipes: AccessRecipe[]): AccessRecipe[] {
    return accessRecipes?.filter((accessRecipe) => this.isValid(accessRecipe));
  }

  static areValid(accessRecipes: AccessRecipe[]): boolean {
    return accessRecipes?.every((accessRecipe) => this.isValid(accessRecipe));
  }

  static isValid(accessRecipe: AccessRecipe): boolean {
    if (!accessRecipe?.filter) return false;
    if (!accessRecipe?.filter?.conditions) return false;
    if (accessRecipe?.filter?.conditions?.length === 0) return false;

    return accessRecipe?.filter?.conditions?.every((condition) => this.isValidFilterCondition(condition));
  }

  static isValidFilterCondition(condition: AccessFilterCondition): boolean {
    if (!condition) return false;
    if (!condition.field?.trim()?.length) return false;
    if (!AccessExpression.isValid(condition.expression)) return false;
    if (condition.values?.length === 0) return false;

    return condition.values?.every((value) => value.trim()?.length);
  }

  static createCondition(
    field: string,
    expression: AccessFilterExpression,
    values?: string[],
    mapping?: string,
  ): AccessFilterCondition {
    if (!field?.trim()?.length) return null;
    if (!AccessExpression.isValid(expression)) return null;

    return {
      field,
      values,
      expression,
      mapping,
    };
  }
}
